import type { InputTargetConversation } from "@/api/conversation-targets";
import type {
  ConversationSelectionReceipt,
  DurableConversation,
} from "@/api/conversations";
import { conversationDisplayName } from "@/lib/conversation-display";
import type { SubagentInputBarTarget } from "@/lib/input-bar-target-kind";
import type { InputBarTarget } from "@/stores/input-bar-target-store";

function pendingPrediction(
  row: Partial<Pick<InputTargetConversation, "pending_next_prompt">>,
): Pick<InputBarTarget, "pendingNextPrompt"> {
  return row.pending_next_prompt
    ? { pendingNextPrompt: row.pending_next_prompt }
    : {};
}

export function durableConversationInputTarget(
  conversation: DurableConversation &
    Partial<Pick<InputTargetConversation, "pending_next_prompt">>,
  selection: ConversationSelectionReceipt,
): InputBarTarget {
  const segment = conversation.frame_runtime_segment;
  const segmentMatches = Boolean(
    segment &&
    (!selection.runtime_session_id ||
      segment.runtime_session_id === selection.runtime_session_id),
  );
  return {
    kind: "chat",
    conversationId: selection.conversation_id,
    runtimeSessionId: selection.runtime_session_id ?? null,
    runtimeGeneration: selection.runtime_generation,
    selectionGeneration: selection.selection_generation,
    agentKey: selection.agent_key,
    projectAgentId: selection.project_agent_id,
    agentName: conversation.agent_name ?? selection.agent_key,
    ticket: conversation.task?.ticket_display ?? null,
    // A task-linked chat keeps its task, so it draws its picker row's icon;
    // `kind: "chat"` still keeps it off the sub-agent path.
    ...(conversation.task_id ? { taskId: conversation.task_id } : {}),
    conversationLabel: conversationDisplayName(conversation),
    projectId: selection.project_id,
    spaceId: selection.project_id,
    projectName: conversation.project_name,
    ...(segment
      ? {
          runtimeAdapterType: segment.adapter_type,
          runtimeModel: segment.model,
          runtimeThinkingEffort: segment.thinking_effort,
          runtimeSource: segment.runtime_source,
          runtimeOverrideExpired: segment.runtime_override_expired,
        }
      : {}),
    ...(segmentMatches
      ? {
          persistedContextPercent: segment?.context_percent,
          persistedContextUsedTokens: segment?.context_used_tokens,
          persistedContextWindowMax: segment?.context_window_max,
        }
      : {}),
    ...pendingPrediction(conversation),
  };
}

/** A native sub-agent row as a direct input bar target (no chat selection). */
export function subagentConversationInputTarget(
  conversation: InputTargetConversation,
): SubagentInputBarTarget | null {
  if (
    conversation.target_kind !== "subagent" ||
    !conversation.task_id ||
    !conversation.project_agent_id
  ) {
    return null;
  }
  return {
    kind: "subagent",
    conversationId: conversation.id,
    taskId: conversation.task_id,
    projectId: conversation.project_id,
    spaceId: conversation.project_id,
    projectAgentId: conversation.project_agent_id,
    agentName: conversation.agent_name ?? "Agent",
    conversationLabel: conversationDisplayName(conversation),
    ticket: conversation.task?.ticket_display ?? null,
    projectName: conversation.project_name,
    ...pendingPrediction(conversation),
  };
}
